/* eslint-disable react/prop-types */
import React from 'react';

import { Box, Typography, CircularProgress, Button } from '@mui/material';
import filesize from 'filesize';
import _ from 'lodash';

import { ReactComponent as CancelIcon } from '../../../common/assets/icons/cancel_icon_gray.svg';
import { ReactComponent as Checkmark } from '../../../common/assets/icons/checkmark_icon_white.svg';
import { ReactComponent as TrashIconWhite } from '../../../common/assets/icons/trash_icon_white.svg';
import { ProcessedOcr } from '../../types/ProcessedOcr';

type Props = {
  ocr: ProcessedOcr;
  handleTappedClear: (file: ProcessedOcr) => void;
};

const BatchFileUploadCell: React.FC<Props> = ({ ocr, handleTappedClear }) => {
  const { wrappedFile } = ocr;
  const StatusIcon: React.FC<{
    ocr: ProcessedOcr;
  }> = ({ ocr }) => {
    const { isUploading, uploadComplete, error } = ocr;
    if (isUploading) {
      return (
        <Box sx={{ marginRight: '15px', alignSelf: 'center' }}>
          <CircularProgress
            variant='indeterminate'
            color='primary'
            size={'30px'}
          />
        </Box>
      );
    }
    if (!_.isEmpty(error)) {
      return (
        <Box
          sx={{
            width: '30px',
            height: '30px',
            borderRadius: '15px',
            backgroundColor: 'error.main',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            marginRight: '10px',
            flexShrink: 0,
          }}
        >
          <CancelIcon />
        </Box>
      );
    }
    if (uploadComplete) {
      return (
        <Box
          sx={{
            width: '30px',
            height: '30px',
            borderRadius: '15px',
            backgroundColor: 'primary.main',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            marginRight: '10px',
            flexShrink: 0,
          }}
        >
          <Checkmark />
        </Box>
      );
    }
    return <></>;
  };

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '402px',
        height: '50px',
        paddingLeft: '15px',
        borderRadius: '10px',
        overflow: 'hidden',
        mt: '15px',
        transition: theme.transitions.create(['all'], {
          duration: theme.transitions.duration.complex,
        }),
        '&:hover': {
          backgroundColor: 'lightBlue.main',
          '&.MuiBox-root > .MuiButton-root > svg': {
            fill: theme.palette.text.primary,
            stroke: theme.palette.text.primary,
          },
          '&.MuiBox-root > .MuiButton-root:hover > svg': {
            fill: theme.palette.common.white,
            stroke: theme.palette.common.white,
          },
        },
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <StatusIcon ocr={ocr} />
        <Box
          sx={{
            display: 'inline-block',
            textOverflow: 'ellipsis',
            minWidth: '100%',
            width: 0,
            paddingRight: '56px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              textAlign: 'left',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography noWrap variant='subtitle2'>
              File name {wrappedFile.name}
            </Typography>
            <Typography noWrap variant='caption' color='textFlorence.main'>
              {filesize(wrappedFile.size ?? 0, { standard: 'jedec' })}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Button
        disabled={ocr.isUploading}
        onClick={() => handleTappedClear(ocr)}
        sx={(theme) => ({
          width: '50px',
          height: '100%',
          backgroundColor: 'error.main',
          display: 'flex',
          opacity: ocr.isUploading ? 0 : 1,
          transition: theme.transitions.create(
            ['background', 'background-color'],
            {
              duration: theme.transitions.duration.complex,
            }
          ),
          '&.MuiButton-root': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'error.main',
            },
          },
        })}
      >
        <TrashIconWhite style={{ marginLeft: 8, flexShrink: 0 }} />
      </Button>
    </Box>
  );
};

export default BatchFileUploadCell;
